import * as THREE from 'three';
import { usedToday, useToday } from '../state/daily';
import type { Interactions } from '../systems/Interactions';
import { roomSlotAt } from '../world/rooms';
import { carWashSign, mudSplat } from '../world/builders/hustleProps';
import { buildRide } from '../world/builders/rides';
import { buildCar, type BuiltCar, type CarKind } from '../world/builders/vehicles';
import type { GameServices } from './services';

export const CAR_WASH = { carsPerDay: 6, opens: 9, closes: 19, splats: 6 } as const;
/** Real seconds to finish a car and still get a tip. */
const TIP_SECONDS = 40;
const FAR = { x: 1e6, z: 1e6 };

/** Who pulls up, in what, and what they say when it's clean. */
const CUSTOMERS: ReadonlyArray<{
  who: string;
  car: CarKind | 'donk' | 'elcamino';
  color: string;
  line: string;
}> = [
  { who: 'Tammy Lynn', car: 'van', color: '#f6d3e2', line: "It's never been this clean. I don't trust it." },
  {
    who: 'Big Earl',
    car: 'pickup',
    color: '#3a6b3a',
    line: "Don't touch the bed. That's where the good mud lives.",
  },
  {
    who: 'Darlene',
    car: 'sedan',
    color: '#e8d27a',
    line: 'Peanut is going to dirty it up in five minutes. Worth it.',
  },
  {
    who: 'Cousin Dale',
    car: 'pickup',
    color: '#8a5a3a',
    line: "It's not stolen. It's borrowed long-term. Thanks, cuz.",
  },
  { who: 'Brenda', car: 'sedan', color: '#c94f7c', line: 'Now THAT is a blowout. For a car.' },
  {
    who: 'Hank',
    car: 'van',
    color: '#efe0cb',
    line: 'Did you check for trackers under there? Never mind. I did.',
  },
  { who: 'Cooter', car: 'donk', color: '#7a2fd0', line: 'Candy paint gotta SHINE, baby. You did good.' },
  { who: 'Lurlene', car: 'elcamino', color: '#2b6cb0', line: 'Smells like a dryer sheet in here. Perfect.' },
  { who: 'Rodeo Randy', car: 'pickup', color: '#b03a2e', line: 'Shinier than my belt buckle. Almost.' },
  {
    who: 'Miss Opal',
    car: 'sedan',
    color: '#9aa7b8',
    line: 'You missed a spot. Kidding. You did not miss a spot.',
  },
];

interface Customer {
  who: string;
  line: string;
  car: BuiltCar;
  splats: THREE.Mesh[];
  /** Where each splat is, for walking up to it. */
  spots: THREE.Vector3[];
  state: 'arriving' | 'parked' | 'leaving';
  collider: number | null;
  t: number;
  parkedAt: number;
  from: THREE.Vector3;
  to: THREE.Vector3;
  heading: number;
}

/**
 * The driveway car wash: hang the sign, and the neighbors pull up out front one at a
 * time. Walk around the car scrubbing off each splatter of mud (E), and they pay up
 * (quick work gets a tip). Pocket change, right at home.
 */
export class CarWash {
  open = false;
  private readonly sign = carWashSign();
  private customer: Customer | null = null;
  private nextIn = 0;
  private signLot = -1;
  private readonly ray = new THREE.Raycaster();
  private readonly tmp = new THREE.Vector3();

  constructor(private readonly s: GameServices) {
    s.world.dynamic.add(this.sign);
    this.placeSign();
  }

  private placeSign(): void {
    const h = this.s.home();
    this.sign.position.set(h.washSign.x, 0, h.washSign.z);
    this.sign.rotation.y = h.washSign.rot;
    this.signLot = h.frame.index;
  }

  register(interactions: Interactions): void {
    const { store } = this.s;
    interactions.add({
      id: 'carwash-sign',
      at: () => this.s.home().washSign,
      radius: 1.8,
      prompt: () => {
        if (this.open) return 'Close up the car wash';
        const left = CAR_WASH.carsPerDay - usedToday(store, 'carwash');
        if (left <= 0) return null;
        return `Open the car wash ($6–12 a car · ${left} left today)`;
      },
      action: () => (this.open ? this.close('closed') : this.openUp()),
      highlight: () => this.sign,
      icon: ':sponge:',
    });
    for (let i = 0; i < CAR_WASH.splats; i++) {
      interactions.add({
        id: `carwash-mud-${i}`,
        at: () => {
          const c = this.customer;
          const sp = c && c.state === 'parked' && c.splats[i]?.visible ? c.spots[i] : null;
          return sp ? { x: sp.x, z: sp.z } : FAR;
        },
        radius: 1.3,
        priority: 1.2,
        prompt: () => 'Scrub off the mud',
        action: () => this.scrub(i),
        highlight: () => this.customer?.splats[i] ?? null,
        icon: ':sponge:',
      });
    }
  }

  private openUp(): void {
    const { store } = this.s;
    const hour = store.minutes / 60;
    if (hour < CAR_WASH.opens || hour >= CAR_WASH.closes) {
      store.toast('Nobody washes a car in the dark. Open up 9 AM to 7 PM.', 'info', ':sponge:');
      return;
    }
    this.open = true;
    this.nextIn = 4;
    this.s.sfx('splash');
    store.toast(
      'Car wash is OPEN. Customers pull up out front. Scrub every splatter of mud (E).',
      'good',
      ':sponge:',
    );
  }

  private close(why: 'closed' | 'wandered' | 'night' | 'done'): void {
    if (!this.open) return;
    this.open = false;
    const c = this.customer;
    if (c && c.state !== 'leaving') this.leave(c);
    const { store } = this.s;
    if (why === 'wandered')
      store.toast(
        `You wandered off and ${c?.who ?? 'the customer'} drove away. The car wash is closed.`,
        'bad',
        ':sponge:',
      );
    else if (why === 'night') store.toast('7 PM: the car wash is closed for the day.', 'info', ':sponge:');
    else if (why === 'done')
      store.toast("That's all the cars today. The neighbors are clean. For now.", 'info', ':sponge:');
  }

  /** New game, load, or a new lot: put the sign up at home, no cars. */
  reset(): void {
    this.open = false;
    if (this.customer) this.remove(this.customer);
    this.placeSign();
  }

  update(dt: number, time: number): void {
    const { store, player } = this.s;
    if (this.s.home().frame.index !== this.signLot) this.reset();
    const c = this.customer;
    if (c) this.animate(c, dt, time);
    if (!this.open) return;
    const sign = this.s.home().washSign;
    if (store.minutes / 60 >= CAR_WASH.closes || store.minutes / 60 < CAR_WASH.opens) {
      this.close('night');
      return;
    }
    // (Inside your trailer for a beer counts as close by: the customer waits.)
    const indoors = roomSlotAt(player.position.x) >= 0;
    if (!indoors && Math.hypot(player.position.x - sign.x, player.position.z - sign.z) > 45) {
      this.close('wandered');
      return;
    }
    if (!this.customer) {
      if (usedToday(store, 'carwash') >= CAR_WASH.carsPerDay) {
        this.close('done');
        return;
      }
      this.nextIn -= dt;
      if (this.nextIn <= 0) this.arrive();
    }
  }

  private arrive(): void {
    const { rng, world } = this.s;
    const who = rng.pick(CUSTOMERS);
    const car =
      who.car === 'donk' || who.car === 'elcamino'
        ? buildRide(who.car)
        : buildCar({ kind: who.car, color: who.color, rusty: rng.chance(0.3) });
    car.group.traverse((o) => (o.userData.noBatch = true));
    const bay = this.s.home().washBay;
    const heading = bay.rot;
    // Cars face +x locally: rotation.y = h points them along (cos h, -sin h).
    const fx = Math.cos(heading);
    const fz = -Math.sin(heading);
    const to = new THREE.Vector3(bay.x, 0, bay.z);
    const from = to.clone().add(new THREE.Vector3(-fx * 24, 0, -fz * 24));
    car.group.position.copy(from);
    car.group.rotation.y = heading;
    world.dynamic.add(car.group);
    this.customer = {
      who: who.who,
      line: who.line,
      car,
      splats: [],
      spots: [],
      state: 'arriving',
      t: 0,
      parkedAt: 0,
      from,
      to,
      heading,
      collider: null,
    };
    this.s.sfx('horn');
  }

  /** Mud goes where the body actually is: cast in from outside and stick it on the paint. */
  private splatter(c: Customer): void {
    const { rng } = this.s;
    const g = c.car.group;
    g.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(c.car.body);
    const local = box.applyMatrix4(g.matrixWorld.clone().invert());
    const lsize = local.getSize(new THREE.Vector3());
    const meshes: THREE.Object3D[] = [];
    c.car.body.traverse((o) => {
      if ((o as THREE.Mesh).isMesh) meshes.push(o);
    });
    // Sides (local ±z), front and back (local ±x), at door and fender height.
    const faces: Array<[THREE.Vector3, number, number]> = [
      [new THREE.Vector3(0, 0, 1), -0.28, 0],
      [new THREE.Vector3(0, 0, 1), 0.25, 0],
      [new THREE.Vector3(0, 0, -1), -0.25, 0],
      [new THREE.Vector3(0, 0, -1), 0.28, 0],
      [new THREE.Vector3(1, 0, 0), 0, 0.2],
      [new THREE.Vector3(-1, 0, 0), 0, -0.2],
    ];
    const q = new THREE.Quaternion().setFromRotationMatrix(
      new THREE.Matrix4().extractRotation(g.matrixWorld),
    );
    for (const [n, along, across] of faces.slice(0, CAR_WASH.splats)) {
      const y = local.min.y + lsize.y * rng.range(0.3, 0.5);
      // A point well outside the car on this side, aimed back at it.
      const lx = n.x !== 0 ? n.x * (lsize.x / 2 + 2) : along * lsize.x;
      const lz = n.z !== 0 ? n.z * (lsize.z / 2 + 2) : across * lsize.z;
      const origin = new THREE.Vector3(lx, y, lz).applyMatrix4(g.matrixWorld);
      const dir = n.clone().negate().applyQuaternion(q);
      this.ray.set(origin, dir);
      const hit = this.ray.intersectObjects(meshes, false)[0];
      const splat = mudSplat(rng.range(0.24, 0.34), rng.next() * 10);
      const normal = n.clone().applyQuaternion(q);
      const at = hit ? hit.point.clone().addScaledVector(normal, 0.015) : origin.addScaledVector(dir, 2);
      splat.position.copy(at);
      splat.lookAt(at.clone().add(normal));
      splat.rotateZ(rng.range(0, Math.PI));
      this.s.world.dynamic.add(splat);
      c.splats.push(splat);
      // Where you stand to reach it: a step out from the car.
      c.spots.push(at.clone().addScaledVector(normal, 0.7).setY(0));
    }
  }

  private animate(c: Customer, dt: number, time: number): void {
    c.t += dt;
    const g = c.car.group;
    if (c.state === 'arriving') {
      const k = Math.min(1, c.t / 3.2);
      const ease = 1 - (1 - k) ** 3;
      g.position.lerpVectors(c.from, c.to, ease);
      for (const w of c.car.wheels) w.rotation.y -= dt * 8 * (1 - k);
      if (k >= 1) {
        c.state = 'parked';
        c.parkedAt = this.s.now();
        // It's parked along the street: you walk around it, not through it.
        c.collider = this.s.world.collision.addRect(c.to.x, c.to.z, c.car.width * 0.9, c.car.length * 0.9);
        this.splatter(c);
        this.s.ui.hud.float(`:car: ${c.who}`, this.tmp.copy(c.to).setY(2.2), '#ffd400');
      }
    } else if (c.state === 'leaving') {
      const k = Math.min(1, c.t / 2.6);
      g.position.lerpVectors(c.from, c.to, k * k);
      for (const w of c.car.wheels) w.rotation.y -= dt * 10 * k;
      if (k >= 1) this.remove(c);
    } else {
      // Parked: a little idle shimmy (it's an old car).
      c.car.body.position.y = Math.abs(Math.sin(time * 1.3)) * 0.004;
    }
  }

  private scrub(i: number): void {
    const c = this.customer;
    const { player } = this.s;
    const splat = c?.splats[i];
    if (!c || !splat || !splat.visible || player.busy) return;
    const p = player.position;
    player.doBusy({
      pose: 'push',
      until: this.s.now() + 0.7,
      facing: Math.atan2(splat.position.x - p.x, splat.position.z - p.z),
      onDone: () => {
        // Closed up (or they gave up) while you were scrubbing: no car, no money.
        if (this.customer !== c || c.state !== 'parked') return;
        splat.visible = false;
        this.s.sfx('splash');
        for (let k = 0; k < 14; k++)
          this.s.world.sparks.spawn({
            x: splat.position.x,
            y: splat.position.y,
            z: splat.position.z,
            vx: (Math.random() - 0.5) * 2,
            vy: Math.random() * 1.5,
            vz: (Math.random() - 0.5) * 2,
            life: 0.9,
            size: 0.18,
            color: '#f4fbff',
            gravity: 3,
          });
        if (c.splats.every((m) => !m.visible)) this.paid(c);
      },
    });
  }

  private paid(c: Customer): void {
    const { store, rng, ui } = this.s;
    const base = rng.int(6, 9);
    const tip = this.s.now() - c.parkedAt < TIP_SECONDS ? rng.int(1, 3) : 0;
    store.incr('carWashes');
    useToday(store, 'carwash');
    store.apply({ money: base + tip, clout: 1 });
    this.s.sfx('cash');
    ui.hud.bubble('carwash', `'${c.line}'`, this.tmp.copy(c.to).setY(2.4), 4);
    store.toast(`${c.who} pays $${base}${tip ? ` plus a $${tip} tip` : ''}. Sparkling.`, 'good', ':sponge:');
    this.leave(c);
  }

  private leave(c: Customer): void {
    c.state = 'leaving';
    c.t = 0;
    this.unblock(c);
    const fx = Math.cos(c.heading);
    const fz = -Math.sin(c.heading);
    c.from = c.car.group.position.clone();
    c.to = c.from.clone().add(new THREE.Vector3(fx * 26, 0, fz * 26));
    for (const m of c.splats) m.removeFromParent();
    c.splats = [];
    c.spots = [];
    this.nextIn = this.s.rng.range(6, 12);
  }

  private unblock(c: Customer): void {
    if (c.collider !== null) this.s.world.collision.remove(c.collider);
    c.collider = null;
  }

  private remove(c: Customer): void {
    this.unblock(c);
    c.car.group.removeFromParent();
    for (const m of c.splats) m.removeFromParent();
    c.car.group.traverse((o) => {
      if (o instanceof THREE.Mesh) o.geometry.dispose();
    });
    if (this.customer === c) this.customer = null;
  }
}
